import { useI18n } from '@ecom/i18n';
import { resolveErrorMessageKey } from '@ecom/i18n/errors';
import {
  Button,
  Card,
  EmptyState,
  Field,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  Tabs,
  TemporaryPasswordPanel,
} from '@ecom/ui-kit';
import { type ReactElement, useState } from 'react';

import { useCreateAdministratorMutation, usePlatformAccountsQuery } from '../api';
import { AccountsTable } from '../components/AccountsTable';
import { CreateAdministratorForm } from '../components/CreateAdministratorForm';
import { PromotionsPanel } from '../components/PromotionsPanel';
import { type CreateAdministratorFormValues } from '../schemas';

import { TableSkeleton } from '@/components/ui/skeletons/TableSkeleton';
import { useToast } from '@/components/ui/toast/ToastProvider';
import { useUserMembershipsLookupMutation } from '@/features/platform-admin/api';
import { MembershipLookupForm } from '@/features/platform-admin/components/MembershipLookupForm';
import { type MembershipLookupFormValues } from '@/features/platform-admin/schemas';
import { consultationReason } from '@/lib/consultation-reason';

type TabId = 'accounts' | 'create' | 'promotions' | 'lookup';

/** What the screen keeps after a creation: the password lives here, in component state, until the superadmin says they noted it. */
interface Issued {
  readonly account: string;
  readonly password: string;
  readonly expiresAt: string;
}

/**
 * The superadmin's view of platform power (lot B1): the accounts that hold a platform role, creating an administrator, removing a role,
 * disabling / re-enabling an account, the two-person promotion to superadmin, and the memberships of any account.
 *
 * Listing the accounts is an AUDITED read: it carries the console's default reason, or the one typed in the field, like every other
 * audited read of this app. Nothing here decides what is allowed - the API and the database functions do, on every request.
 */
export function AdministratorsPage(): ReactElement {
  const { t, locale } = useI18n();
  const toast = useToast();
  const [tab, setTab] = useState<TabId>('accounts');
  const [reason, setReason] = useState('');
  const [issued, setIssued] = useState<Issued | null>(null);
  const listReason = consultationReason(reason);

  const accountsQuery = usePlatformAccountsQuery(listReason);
  const createMutation = useCreateAdministratorMutation();
  const lookupMutation = useUserMembershipsLookupMutation();
  const accounts = accountsQuery.data?.accounts ?? [];

  async function onCreate(values: CreateAdministratorFormValues): Promise<void> {
    try {
      const created = await createMutation.mutateAsync({ ...values, locale });
      toast.success(t('admin.accounts.create.success'));
      setIssued({
        account: values.email,
        password: created.temporaryPassword,
        expiresAt: created.expiresAt,
      });
    } catch (error) {
      toast.error(t(resolveErrorMessageKey(error)));
      // Re-thrown so the form keeps what was typed.
      throw error;
    }
  }

  async function onLookup(values: MembershipLookupFormValues): Promise<void> {
    try {
      await lookupMutation.mutateAsync(values);
    } catch (error) {
      toast.error(t(resolveErrorMessageKey(error)));
    }
  }

  const needsReason = tab === 'accounts' || tab === 'promotions';

  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-6">
      <div>
        <h1 className="font-display text-2xl font-bold text-ink">{t('admin.accounts.title')}</h1>
        <p className="text-sm text-inkFaint">{t('admin.accounts.subtitle')}</p>
      </div>

      <Tabs
        label={t('admin.accounts.tab.label')}
        tabs={[
          { id: 'accounts', label: t('admin.accounts.tab.accounts') },
          { id: 'create', label: t('admin.accounts.tab.create') },
          { id: 'promotions', label: t('admin.accounts.tab.promotions') },
          { id: 'lookup', label: t('admin.accounts.tab.lookup') },
        ]}
        activeId={tab}
        onChange={(id) => {
          setTab(id as TabId);
        }}
      >
        <div className="flex flex-col gap-5">
          {needsReason ? (
            <div className="rounded-xl border border-line bg-surface p-4">
              <Field label={t('admin.common.reasonConsultationLabel')}>
                {(bind) => (
                  <input
                    {...bind}
                    type="text"
                    value={reason}
                    onChange={(event) => {
                      setReason(event.target.value);
                    }}
                    maxLength={500}
                    className="ecom-input"
                    placeholder={t('admin.accounts.reasonPlaceholder')}
                  />
                )}
              </Field>
            </div>
          ) : null}

          {tab === 'accounts' ? (
            accountsQuery.isPending ? (
              <TableSkeleton columns={5} />
            ) : accountsQuery.isError ? (
              <EmptyState title={t('admin.accounts.loadError')} />
            ) : accounts.length === 0 ? (
              <EmptyState title={t('admin.accounts.empty')} />
            ) : (
              <AccountsTable accounts={accounts} />
            )
          ) : null}

          {tab === 'create' ? (
            issued === null ? (
              <Card>
                <CreateAdministratorForm
                  onSubmit={onCreate}
                  isSubmitting={createMutation.isPending}
                />
              </Card>
            ) : (
              <Card>
                <div className="flex flex-col gap-4">
                  <p className="text-sm text-inkSoft">{t('admin.accounts.create.handover')}</p>
                  <TemporaryPasswordPanel
                    account={issued.account}
                    password={issued.password}
                    expiresAt={issued.expiresAt}
                    onDone={() => {
                      setIssued(null);
                    }}
                    onExpire={() => {
                      setIssued({ ...issued, password: '' });
                    }}
                  />
                  {issued.password === '' ? (
                    <Button
                      variant="secondary"
                      onClick={() => {
                        setIssued(null);
                      }}
                    >
                      {t('admin.accounts.create.createAnother')}
                    </Button>
                  ) : null}
                </div>
              </Card>
            )
          ) : null}

          {tab === 'promotions' ? (
            <PromotionsPanel reason={listReason} accounts={accounts} />
          ) : null}

          {tab === 'lookup' ? (
            <section className="flex flex-col gap-3 rounded-xl border border-line bg-surface p-5">
              <p className="text-sm text-inkFaint">{t('admin.roles.subtitle')}</p>
              <MembershipLookupForm
                onSubmit={(values) => {
                  void onLookup(values);
                }}
                isSubmitting={lookupMutation.isPending}
              />
              {lookupMutation.isSuccess ? (
                lookupMutation.data.memberships.length === 0 ? (
                  <EmptyState title={t('admin.roles.noMembershipsTitle')} />
                ) : (
                  <Table caption={t('admin.roles.membershipsTableCaption')}>
                    <TableHead>
                      <TableRow>
                        <TableHeaderCell>{t('admin.roles.table.colTenant')}</TableHeaderCell>
                        <TableHeaderCell>{t('merchant.staff.table.colRole')}</TableHeaderCell>
                        <TableHeaderCell>{t('merchant.orders.table.colStatus')}</TableHeaderCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {lookupMutation.data.memberships.map((membership) => (
                        <TableRow key={membership.id}>
                          <TableCell>
                            <span className="font-mono text-xs text-inkFaint">
                              {membership.tenant_id}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-ink">{membership.role_key}</span>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-ink">{membership.status}</span>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )
              ) : null}
            </section>
          ) : null}
        </div>
      </Tabs>
    </div>
  );
}
